'use client';

import { useRef, useState, useLayoutEffect, useMemo } from 'react';
import { AbsoluteFill, Easing, interpolate } from 'remotion';
import { fade } from '@remotion/transitions/fade';
import { slide, SlideDirection } from '@remotion/transitions/slide';
import { wipe, WipeDirection } from '@remotion/transitions/wipe';
import { flip, FlipDirection } from '@remotion/transitions/flip';
import { clockWipe } from '@remotion/transitions/clock-wipe';
import { iris } from '@remotion/transitions/iris';
import type { TransitionPresentationComponentProps } from '@remotion/transitions';
import {
  TransitionType,
  TransitionDirection,
  DEFAULT_TRANSITION_DIRECTION,
} from './transition-drag-data';
import {
  CAPSULE_COVER_PRIMARY_COLOR,
  CAPSULE_COVER_SECONDARY_COLOR,
} from '../../../../items/transition/capsule-cover-transition';

interface TransitionPreviewProps {
  type: TransitionType;
  direction?: TransitionDirection;
  progress: number; // 0 ~ 1
  previewDurationInFrames?: number;
  primaryColor?: string;
  secondaryColor?: string;
}

const DEFAULT_PREVIEW_DURATION_IN_FRAMES = 30;
const CAPSULE_ROW_COUNT = 7;

function CapsuleCoverPreview({
  progress,
  primaryColor,
  secondaryColor,
}: {
  progress: number;
  primaryColor: string;
  secondaryColor: string;
}) {
  return (
    <AbsoluteFill>
      {progress >= 0.5 ? <SceneB /> : <SceneA />}
      {Array.from({ length: CAPSULE_ROW_COUNT }, (_, index) => {
        const direction = index % 2 === 0 ? 1 : -1;
        const stagger = index * 0.018;
        const enterProgress = interpolate(progress, [stagger, 0.48], [0, 1], {
          extrapolateLeft: 'clamp',
          extrapolateRight: 'clamp',
          easing: Easing.out(Easing.cubic),
        });
        const exitProgress = interpolate(
          progress,
          [0.52 + (CAPSULE_ROW_COUNT - index - 1) * 0.018, 1],
          [0, 1],
          {
            extrapolateLeft: 'clamp',
            extrapolateRight: 'clamp',
            easing: Easing.in(Easing.cubic),
          },
        );
        const translateX =
          direction * (1 - enterProgress) * -125 +
          direction * exitProgress * 125;
        return (
          <div
            key={index}
            style={{
              position: 'absolute',
              top: `${(index * 100) / CAPSULE_ROW_COUNT - 1}%`,
              left: '-12.5%',
              width: '125%',
              height: `${100 / CAPSULE_ROW_COUNT + 2.2}%`,
              borderRadius: '9999px',
              backgroundColor: index % 3 === 1 ? secondaryColor : primaryColor,
              transform: `translateX(${translateX}%)`,
            }}
          />
        );
      })}
    </AbsoluteFill>
  );
}

// A 장면 (파란색 배경)
function SceneA() {
  return (
    <AbsoluteFill
      style={{
        backgroundColor: '#3B82F6',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <span
        style={{
          color: 'white',
          fontSize: 32,
          fontWeight: 'bold',
        }}
      >
        A
      </span>
    </AbsoluteFill>
  );
}

// B 장면 (주황색 배경)
function SceneB() {
  return (
    <AbsoluteFill
      style={{
        backgroundColor: '#F97316',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <span
        style={{
          color: 'white',
          fontSize: 32,
          fontWeight: 'bold',
        }}
      >
        B
      </span>
    </AbsoluteFill>
  );
}

// Transition presentation 객체를 가져오는 함수
function getTransitionPresentation(
  type: TransitionType,
  width: number,
  height: number,
  direction?: TransitionDirection,
) {
  const dir = direction ?? DEFAULT_TRANSITION_DIRECTION;

  switch (type) {
    case 'fade':
      return fade();
    case 'slide':
      return slide({ direction: dir as SlideDirection });
    case 'wipe':
      return wipe({ direction: dir as WipeDirection });
    case 'flip':
      return flip({ direction: dir as FlipDirection });
    case 'clockWipe':
      return clockWipe({ width, height });
    case 'iris':
      return iris({ width, height });
    default:
      return fade();
  }
}

export default function TransitionPreview({
  type,
  direction,
  progress,
  previewDurationInFrames = DEFAULT_PREVIEW_DURATION_IN_FRAMES,
  primaryColor = CAPSULE_COVER_PRIMARY_COLOR,
  secondaryColor = CAPSULE_COVER_SECONDARY_COLOR,
}: TransitionPreviewProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [size, setSize] = useState({ width: 120, height: 80 });

  // 컨테이너 크기 측정 (ResizeObserver로 크기 변화 감지)
  useLayoutEffect(() => {
    const element = containerRef.current;
    if (!element) return;

    const updateSize = () => {
      const { offsetWidth, offsetHeight } = element;
      // 크기가 0이면 업데이트하지 않음 (마운트 전 상태)
      if (offsetWidth > 0 && offsetHeight > 0) {
        setSize({ width: offsetWidth, height: offsetHeight });
      }
    };

    // 초기 크기 설정
    updateSize();

    // ResizeObserver로 크기 변화 감지
    const resizeObserver = new ResizeObserver(updateSize);
    resizeObserver.observe(element);

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  // 실제 크기로 presentation 생성
  const presentation = useMemo(
    () => getTransitionPresentation(type, size.width, size.height, direction),
    [type, size.width, size.height, direction],
  );

  // Remotion transition presentation 사용
  const PresentationComponent = presentation.component as React.ComponentType<
    TransitionPresentationComponentProps<typeof presentation.props>
  >;

  return (
    <div
      ref={containerRef}
      style={{
        width: '100%',
        height: '100%',
        position: 'relative',
        overflow: 'hidden',
        backgroundColor: 'transparent',
      }}
    >
      {type === 'capsuleCover' ? (
        <CapsuleCoverPreview
          progress={progress}
          primaryColor={primaryColor}
          secondaryColor={secondaryColor}
        />
      ) : (
        <AbsoluteFill>
          {/* 나가는 장면 (A) - exiting */}
          <PresentationComponent
            presentationDirection="exiting"
            presentationProgress={progress}
            presentationDurationInFrames={previewDurationInFrames}
            passedProps={presentation.props}
            onElementImage={() => {}}
            onUnmount={() => {}}
            bothEnteringAndExiting={false}
          >
            <SceneA />
          </PresentationComponent>
          {/* 들어오는 장면 (B) - entering */}
          <PresentationComponent
            presentationDirection="entering"
            presentationProgress={progress}
            presentationDurationInFrames={previewDurationInFrames}
            passedProps={presentation.props}
            onElementImage={() => {}}
            onUnmount={() => {}}
            bothEnteringAndExiting={false}
          >
            <SceneB />
          </PresentationComponent>
        </AbsoluteFill>
      )}
    </div>
  );
}
